import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ErrorPage403 from '@/app/routes/errorPage403';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { Column, Pagination, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import { TabToggle, type TabItem } from '@shared/components/TabToggle';
import { Tooltip } from '@shared/components/Tooltip';
import { Menu, MenuSize, MenuVariant } from '@shared/components/Menu';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { getInitials } from '@shared/utils/getInitials';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import myRequestsEmptyAnimation from '@/assets/lottie/noDataAnimation.json';
import { CERTIFICATE_ERROR_MESSAGES } from '../../errors/errorMessages';
import { CERTIFICATE_LABELS } from '../../constants/labels';
import type { CertificateTemplateListItem } from '../../types';
import type { CertificateListItem } from '../../types/myCertificateTypes';
import { useCertificateTemplateList } from './useCertificateTemplateList';
import { AsyncMultiSelectDropdown } from '@shared/components/Dropdown/AsyncMultiSelectDropdown';
import { useCertificateAdminList } from './useCertificateAdminList';
import { useClientCertificateList } from './useClientCertificateList';
import { CertificateExportSnackbars } from '../../components/CertificateExportSnackbars';
import { ExportButton } from '../../components/ExportButton';
import { useCertificateExport } from '../../hooks/useCertificateExport';
import {
  buildExportParams,
  countAppliedFilters,
} from '../../utils/certificateFilters';

type ActiveTab = 'szablony' | 'certyfikaty';

const TABS: TabItem<ActiveTab>[] = [
  { id: 'szablony', label: CERTIFICATE_LABELS.TAB_TEMPLATES },
  { id: 'certyfikaty', label: CERTIFICATE_LABELS.TAB_CERTIFICATES },
];

const MIN_SPACE_BELOW_FOR_MENU = 70;

const formatCreatedAt = (iso: string | null | undefined): string => {
  if (!iso) {
    return '—';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '—';
  }
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')}, ${get('hour')}:${get('minute')}:${get('second')}`;
};

/**
 * Technician table cell — circular avatar (profile photo when available, else
 * initials) followed by the technician's name. Mirrors the avatar pattern used
 * across the other list flows (e.g. service-request tables).
 */
function TechnicianCell({
  name,
  url,
}: {
  name: string | null | undefined;
  url: string | null | undefined;
}) {
  const [imgError, setImgError] = useState(false);
  const safeName = name ?? '';
  const showImage = !!url && !imgError;

  if (!safeName.trim()) {
    return (
      <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px]">
        —
      </span>
    );
  }

  return (
    <div className="flex items-center gap-2 w-full min-w-0">
      <div className="w-8 h-8 rounded-full bg-[#E8EEF0] flex items-center justify-center shrink-0 overflow-hidden">
        {showImage ? (
          <img
            src={url ?? undefined}
            alt={safeName}
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="text-[#223746] text-sm font-medium leading-none">
            {getInitials(safeName)}
          </span>
        )}
      </div>
      {/* min-w-0 + flex-1 lets the name shrink below its intrinsic width so the
          span can ellipsis; without it the flex row keeps the avatar's sibling
          at min-content and the text overflows uncut. */}
      <div className="min-w-0 flex-1">
        <Tooltip content={safeName} align="center">
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
            {safeName}
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

const DotsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
      stroke="#474A4C"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Per-instance state/refs the certificate-list columns close over. Passed in so
 * the admin and client tabs share a single column definition instead of two
 * near-identical copies.
 */
interface CertificateColumnsDeps {
  /** Gates the row "view" action. Client passes true (always allowed). */
  canView: boolean;
  openMenuRowId: string | null;
  setOpenMenuRowId: Dispatch<SetStateAction<string | null>>;
  menuOpensUpward: boolean;
  setMenuOpensUpward: Dispatch<SetStateAction<boolean>>;
  menuAnchorRef: RefObject<HTMLDivElement | null>;
  tableScrollContainerRef: RefObject<HTMLDivElement | null>;
  navigate: ReturnType<typeof useNavigate>;
}

/**
 * Builds the certificate table columns (issue-date sortable) shared by the
 * admin/super-admin and client tabs. The only per-tab difference is whether the
 * "view" action is gated by a privilege (canView).
 */
function buildCertificateColumns({
  canView,
  openMenuRowId,
  setOpenMenuRowId,
  menuOpensUpward,
  setMenuOpensUpward,
  menuAnchorRef,
  tableScrollContainerRef,
  navigate,
}: CertificateColumnsDeps): Column<CertificateListItem>[] {
  return [
    {
      key: 'certificate_id',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_CERT_ID,
      minWidth: '200px',
      maxWidth: '240px',
      render: (row) => (
        <div className="flex items-center w-full min-w-0">
          <Tooltip content={row.certificate_id}>
            <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.certificate_id}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'equipment_business_id',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_EQUIPMENT_ID,
      minWidth: '168px',
      maxWidth: '200px',
      render: (row) => (
        <div className="flex items-center w-full min-w-0">
          <Tooltip content={row.equipment_business_id ?? '—'}>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.equipment_business_id ?? '—'}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'equipment_name',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_EQUIPMENT_NAME,
      minWidth: '224px',
      maxWidth: '260px',
      render: (row) => (
        <div className="flex items-center w-full min-w-0">
          <Tooltip content={row.equipment_name ?? '—'} align="center">
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.equipment_name ?? '—'}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'service_request_id',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_SR_ID,
      minWidth: '260px',
      maxWidth: '300px',
      render: (row) => (
        <div className="flex items-center w-full min-w-0">
          <Tooltip content={row.service_request_id}>
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.service_request_id}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'service_type_name',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_SR_NAME,
      minWidth: '248px',
      maxWidth: '280px',
      render: (row) => (
        <div className="flex items-center w-full min-w-0">
          <Tooltip content={row.service_type_name} align="center">
            <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
              {row.service_type_name}
            </span>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'generated_at',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_ISSUE_DATE,
      minWidth: '220px',
      showSortIcon: true,
      render: (row) => (
        <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap block">
          {formatCreatedAt(row.generated_at)}
        </span>
      ),
    },
    {
      key: 'technician_name',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_TECHNICIAN,
      minWidth: '256px',
      maxWidth: '300px',
      render: (row) => (
        <TechnicianCell
          name={row.technician_name}
          url={row.technician_profile_photo_url}
        />
      ),
    },
    {
      key: 'actions',
      header: CERTIFICATE_LABELS.CERTS_TAB_COL_ACTIONS,
      width: '80px',
      stickyRight: true,
      stickyRightBg: '#ffffff',
      centerHeader: true,
      render: (row, index) => {
        const isOpen = openMenuRowId === row.id;
        return (
          <div
            ref={isOpen ? menuAnchorRef : undefined}
            className="relative inline-flex items-center justify-center w-full"
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const willOpen = openMenuRowId !== row.id;
                if (willOpen) {
                  const buttonRect = (
                    e.currentTarget as HTMLButtonElement
                  ).getBoundingClientRect();
                  const containerRect =
                    tableScrollContainerRef.current?.getBoundingClientRect();
                  const visibleBottom = containerRect
                    ? containerRect.bottom
                    : window.innerHeight;
                  setMenuOpensUpward(
                    visibleBottom - buttonRect.bottom < MIN_SPACE_BELOW_FOR_MENU
                  );
                }
                setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
              }}
              aria-label={`${CERTIFICATE_LABELS.CERTS_TAB_COL_ACTIONS} ${row.certificate_id}`}
              aria-expanded={isOpen}
              aria-haspopup="menu"
              data-testid={`cert-action-menu-button-${index ?? 0}`}
              className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            >
              <DotsIcon />
            </button>
            {isOpen && (
              <div
                className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                role="presentation"
              >
                <Menu
                  items={[
                    {
                      id: 'VIEW',
                      label: CERTIFICATE_LABELS.CERTS_TAB_CARD_VIEW,
                      disabled: !canView,
                      disabledTooltip:
                        CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                      disabledTooltipSide: 'left',
                    },
                  ]}
                  variant={MenuVariant.Light}
                  size={MenuSize.Small}
                  toolTip={false}
                  onItemClick={(itemId) => {
                    setOpenMenuRowId(null);
                    if (itemId === 'VIEW') {
                      navigate(`/Moje-certyfikaty/${row.id}`);
                    }
                  }}
                  showLeadingIcons={false}
                />
              </div>
            )}
          </div>
        );
      },
    },
  ];
}

interface TemplatesTabProps {
  onCreateClick: () => void;
  canCreate: boolean;
  canView: boolean;
  canEdit: boolean;
}

function TemplatesTab({
  onCreateClick,
  canCreate,
  canView,
  canEdit,
}: TemplatesTabProps) {
  const navigate = useNavigate();
  const {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
  } = useCertificateTemplateList();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);

  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleApplyAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = useCallback(() => {
    setDateRange(appliedFilters.dateRange);
    setIsFilterOpen(false);
  }, [appliedFilters.dateRange, setDateRange]);

  useEffect(() => {
    if (!isFilterOpen) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isFilterOpen, handleCloseFilter]);

  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openMenuRowId]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    if ((appliedFilters.dateRange.startDate ?? '').trim()) {
      count += 1;
    }
    if ((appliedFilters.dateRange.endDate ?? '').trim()) {
      count += 1;
    }
    return count;
  }, [appliedFilters.dateRange]);

  const columns: Column<CertificateTemplateListItem>[] = useMemo(
    () => [
      {
        key: 'certTemplateName',
        header: CERTIFICATE_LABELS.LIST_COL_CERTIFICATE_NAME,
        minWidth: '360px',
        maxWidth: '380px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.certTemplateName} align="center">
              <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.certTemplateName}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'certTemplateId',
        header: CERTIFICATE_LABELS.LIST_COL_TEMPLATE_ID,
        minWidth: '200px',
        maxWidth: '240px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.certTemplateId}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.certTemplateId}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'formTemplateName',
        header: CERTIFICATE_LABELS.LIST_COL_TASK_FORM_NAME,
        minWidth: '380px',
        maxWidth: '400px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.formTemplateName} align="center">
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.formTemplateName}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'formTemplateId',
        header: CERTIFICATE_LABELS.LIST_COL_TASK_FORM_ID,
        minWidth: '200px',
        maxWidth: '240px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.formTemplateId}>
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.formTemplateId}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'createdAt',
        header: CERTIFICATE_LABELS.LIST_COL_CREATED_AT,
        minWidth: '200px',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap block">
            {formatCreatedAt(row.createdAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: CERTIFICATE_LABELS.LIST_COL_ACTIONS,
        width: '80px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        centerHeader: true,
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    setMenuOpensUpward(
                      visibleBottom - buttonRect.bottom <
                        MIN_SPACE_BELOW_FOR_MENU
                    );
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                aria-label={`${CERTIFICATE_LABELS.LIST_COL_ACTIONS} ${row.certTemplateName}`}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                data-testid={`action-menu-button-${index ?? 0}`}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
              >
                <DotsIcon />
              </button>
              {isOpen && (
                <div
                  className={`absolute right-0 z-50 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                >
                  <Menu
                    items={[
                      {
                        id: 'VIEW',
                        label: CERTIFICATE_LABELS.LIST_ACTION_VIEW,
                        disabled: !canView,
                        disabledTooltip:
                          CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                        disabledTooltipSide: 'left',
                      },
                      {
                        id: 'EDIT',
                        label: CERTIFICATE_LABELS.LIST_ACTION_EDIT,
                        disabled: !canEdit,
                        disabledTooltip:
                          CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
                        disabledTooltipSide: 'left',
                      },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(`/Certyfikaty/Podgląd-szablonu/${row.id}`);
                      } else if (itemId === 'EDIT') {
                        navigate(`/Certyfikaty/Edycja-szablonu/${row.id}`, {
                          state: { returnTo: '/Certyfikaty' },
                        });
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowId, menuOpensUpward, navigate, canView, canEdit]
  );

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasSearch = search.trim() !== '';
  const hasDateFilter =
    (appliedFilters.dateRange.startDate ?? '').trim() !== '' ||
    (appliedFilters.dateRange.endDate ?? '').trim() !== '';

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    items.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !hasSearch &&
    !hasDateFilter;

  const showNoResults =
    !loading &&
    !isSearchClearing &&
    !hasNoData &&
    items.length === 0 &&
    (hasSearch || hasDateFilter);

  const noResultsDescription =
    hasSearch && hasDateFilter
      ? CERTIFICATE_LABELS.NO_RESULTS_DESCRIPTION_BOTH
      : hasSearch
        ? CERTIFICATE_LABELS.NO_RESULTS_DESCRIPTION_SEARCH
        : CERTIFICATE_LABELS.NO_RESULTS_DESCRIPTION_FILTER;

  return (
    <>
      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={CERTIFICATE_LABELS.LIST_SEARCH_PLACEHOLDER}
          />
          <div className="flex items-center gap-2">
            <Tooltip
              content={CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
              side="left"
              align="center"
              forceShow={!canCreate}
              triggerTabIndex={-1}
              triggerWrapContent
            >
              <button
                type="button"
                onClick={canCreate ? onCreateClick : undefined}
                disabled={!canCreate}
                data-testid="create-certificate-template-toolbar-button"
                className={`h-10 px-4 py-2.5 rounded-lg flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] ${canCreate ? 'bg-[#2A4455] hover:bg-[#1f3340] transition-colors cursor-pointer' : 'bg-[#2A4455] opacity-38 cursor-not-allowed'}`}
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px] whitespace-nowrap">
                  {CERTIFICATE_LABELS.BUTTON_CREATE_TEMPLATE}
                </span>
              </button>
            </Tooltip>
            <div
              ref={filterContainerRef}
              className={`dropdown ${isFilterOpen ? 'show' : ''}`}
            >
              <FilterBar
                onClick={() => setIsFilterOpen((prev) => !prev)}
                isOpen={isFilterOpen}
                filterCount={appliedFilterCount}
              />
              <FilterPopup
                isOpen={isFilterOpen}
                onClose={handleCloseFilter}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                showDateRange
                onApply={handleApplyAndClose}
                onClear={handleClearFilters}
              />
            </div>
          </div>
        </div>
      )}

      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
      >
        {hasNoData ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-6 pb-16">
            <LottieAnimation
              animationData={myRequestsEmptyAnimation}
              loop
              width={300}
              aspectRatio="1 / 1"
            />
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-base font-medium leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.EMPTY_STATE_TITLE}
              </p>
              <p className="text-base font-normal leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.EMPTY_STATE_DESCRIPTION}
              </p>
            </div>
            <Tooltip
              content={CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
              side="bottom"
              align="center"
              forceShow={!canCreate}
              triggerTabIndex={-1}
              triggerWrapContent
            >
              <button
                type="button"
                onClick={canCreate ? onCreateClick : undefined}
                disabled={!canCreate}
                data-testid="create-certificate-template-button"
                className={`h-10 px-4 py-2.5 rounded-lg flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455] ${canCreate ? 'bg-[#2A4455] hover:bg-[#1f3340] transition-colors cursor-pointer' : 'bg-[#2A4455] opacity-38 cursor-not-allowed'}`}
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  {CERTIFICATE_LABELS.BUTTON_CREATE_TEMPLATE}
                </span>
              </button>
            </Tooltip>
          </div>
        ) : showNoResults ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-3">
            <LottieAnimation
              animationData={noResultsAnimation}
              width="clamp(160px, 18vw, 360px)"
              aspectRatio="432 / 350"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                {CERTIFICATE_LABELS.NO_RESULTS_TITLE}
              </p>
              <p className="text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                {noResultsDescription}
              </p>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={items}
            loading={loading || isSearchClearing}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            getStickyCellZIndex={(row, _idx, columnKey) =>
              columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
            }
            tableBodyStyle={{ gap: 4 }}
          />
        )}
      </div>

      {!hasNoData && totalCount > 10 && (
        <div className="shrink-0 px-4 -mx-4">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <p className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </p>
          )}
        </div>
      )}
    </>
  );
}

interface CertificatesTabProps {
  canView: boolean;
}

function CertificatesTab({ canView }: CertificatesTabProps) {
  const navigate = useNavigate();
  const {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    equipmentIds,
    setEquipmentIds,
    technicianIds,
    setTechnicianIds,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    sortColumn,
    sortOrder,
    handleSort,
    equipmentOptions,
    equipmentOptionsLoading,
    equipmentOptionsLoadingMore,
    loadMoreEquipment,
    handleEquipmentSearchChange,
    technicianOptions,
    technicianOptionsLoading,
    technicianOptionsLoadingMore,
    loadMoreTechnicians,
    handleTechnicianSearchChange,
  } = useCertificateAdminList();

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  // Same gating as the client tab: export needs the certificate-list
  // privilege; while privileges load the button is disabled without the
  // no-permission tooltip.
  const canExport = privileges.certificateList ?? false;
  const exportBlocked = !privilegesLoading && !canExport;

  const exportFlow = useCertificateExport();
  const { isExporting, handleExport: runExport } = exportFlow;

  // Exports every row matching the committed search + filters — identical
  // params to the list fetch, minus pagination (export covers all rows).
  const handleExport = useCallback(async () => {
    await runExport(buildExportParams(appliedFilters), items.length);
  }, [appliedFilters, items.length, runExport]);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);

  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  const filterCount = useMemo(
    () => countAppliedFilters(appliedFilters),
    [appliedFilters]
  );

  const handleApplyAndClose = useCallback(() => {
    handleApplyFilters();
    setIsFilterOpen(false);
  }, [handleApplyFilters]);

  const handleClearAndClose = useCallback(() => {
    handleClearFilters();
    setIsFilterOpen(false);
  }, [handleClearFilters]);

  useEffect(() => {
    if (!isFilterOpen) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest?.('[data-date-picker-portal]')
      ) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isFilterOpen]);

  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openMenuRowId]);

  const columns: Column<CertificateListItem>[] = useMemo(
    () =>
      buildCertificateColumns({
        canView,
        openMenuRowId,
        setOpenMenuRowId,
        menuOpensUpward,
        setMenuOpensUpward,
        menuAnchorRef,
        tableScrollContainerRef,
        navigate,
      }),
    [openMenuRowId, menuOpensUpward, navigate, canView]
  );

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasSearch = search.trim() !== '';
  const hasFilter = filterCount > 0;

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    items.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !hasSearch &&
    !hasFilter;

  const showNoResults =
    !loading &&
    !isSearchClearing &&
    !hasNoData &&
    items.length === 0 &&
    (hasSearch || hasFilter);

  const filterDropdowns = (
    <>
      {/* Equipment name filter */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-[#181A1B] leading-5 tracking-[0.10px] wrap-break-word">
          {CERTIFICATE_LABELS.CERTS_TAB_FILTER_EQUIPMENT_LABEL}
        </p>
        <AsyncMultiSelectDropdown
          label={CERTIFICATE_LABELS.CERTS_TAB_FILTER_EQUIPMENT_LABEL}
          items={equipmentOptions.map((opt) => ({
            id: opt.id,
            label: `${opt.name} [${opt.equipment_id}]`,
          }))}
          selectedIds={equipmentIds}
          loading={equipmentOptionsLoading}
          loadingMore={equipmentOptionsLoadingMore}
          onSelectionChange={setEquipmentIds}
          onSearchChange={handleEquipmentSearchChange}
          onScrollEnd={loadMoreEquipment}
          toolTip={false}
        />
      </div>

      {/* Technician name filter */}
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-[#181A1B] leading-5 tracking-[0.10px] wrap-break-word">
          {CERTIFICATE_LABELS.CERTS_TAB_FILTER_TECHNICIAN_LABEL}
        </p>
        <AsyncMultiSelectDropdown
          label={CERTIFICATE_LABELS.CERTS_TAB_FILTER_TECHNICIAN_LABEL}
          items={technicianOptions.map((opt) => ({
            id: opt.id,
            label: `${opt.name} [${opt.technician_id}]`,
          }))}
          selectedIds={technicianIds}
          loading={technicianOptionsLoading}
          loadingMore={technicianOptionsLoadingMore}
          onSelectionChange={setTechnicianIds}
          onSearchChange={handleTechnicianSearchChange}
          onScrollEnd={loadMoreTechnicians}
          toolTip={false}
        />
      </div>
    </>
  );

  return (
    <>
      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={CERTIFICATE_LABELS.CERTS_TAB_SEARCH_PLACEHOLDER}
          />
          <div className="flex items-center gap-2 shrink-0">
            <div ref={filterContainerRef} className="relative">
              <FilterBar
                onClick={() => setIsFilterOpen((prev) => !prev)}
                isOpen={isFilterOpen}
                filterCount={filterCount}
              />
              <FilterPopup
                isOpen={isFilterOpen}
                onClose={() => {
                  setIsFilterOpen(false);
                  setEquipmentIds(appliedFilters.equipmentIds);
                  setTechnicianIds(appliedFilters.technicianIds);
                  setDateRange(appliedFilters.dateRange);
                  handleEquipmentSearchChange('');
                  handleTechnicianSearchChange('');
                }}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                dropdown={filterDropdowns}
                dropdownHasValue={
                  equipmentIds.length > 0 || technicianIds.length > 0
                }
                onApply={handleApplyAndClose}
                onClear={handleClearAndClose}
              />
            </div>
            <ExportButton
              onClick={() => void handleExport()}
              disabled={!canExport || isExporting || items.length === 0}
              disabledTooltip={
                exportBlocked
                  ? CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP
                  : undefined
              }
              colorClassName="bg-[#2A4455] text-white hover:bg-[#1f3340] hover:disabled:bg-[#2A4455]"
              iconColor="#FFFFFF"
            />
          </div>
        </div>
      )}

      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
      >
        {hasNoData ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-6 pb-16">
            <LottieAnimation
              animationData={myRequestsEmptyAnimation}
              loop
              width={300}
              aspectRatio="1 / 1"
            />
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-base font-medium leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.CERTS_TAB_EMPTY_TITLE}
              </p>
              <p className="text-base font-normal leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.CERTS_TAB_EMPTY_DESC}
              </p>
            </div>
          </div>
        ) : showNoResults ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-3">
            <LottieAnimation
              animationData={noResultsAnimation}
              width="clamp(160px, 18vw, 360px)"
              aspectRatio="432 / 350"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                {CERTIFICATE_LABELS.CERTS_TAB_NO_RESULTS_TITLE}
              </p>
              <p className="text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                {CERTIFICATE_LABELS.CERTS_TAB_NO_RESULTS_DESC}
              </p>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={items}
            loading={loading || isSearchClearing}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            onSort={handleSort}
            sortColumn={sortColumn}
            sortDirection={sortOrder}
            getStickyCellZIndex={(row, _idx, columnKey) =>
              columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
            }
            tableBodyStyle={{ gap: 4 }}
          />
        )}
      </div>

      {!hasNoData && totalCount > 10 && (
        <div className="shrink-0 px-4 -mx-4">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <p className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </p>
          )}
        </div>
      )}

      <CertificateExportSnackbars {...exportFlow} />
    </>
  );
}

interface ClientCertificatesTabProps {
  onAccessDenied?: (() => void) | undefined;
}

function ClientCertificatesTab({ onAccessDenied }: ClientCertificatesTabProps) {
  const navigate = useNavigate();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  // Export requires the download-certificate privilege. While privileges are
  // still loading the button is just disabled; the no-permission tooltip only
  // appears once we know for sure.
  const canExport = privileges.canDownloadCertificate ?? false;
  const exportBlocked = !privilegesLoading && !canExport;

  const {
    items,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    fetchError,
    accessDenied,
    search,
    isSearchClearing,
    setSearch,
    equipmentIds,
    setEquipmentIds,
    technicianIds,
    setTechnicianIds,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    sortColumn,
    sortOrder,
    handleSort,
    equipmentOptions,
    equipmentOptionsLoading,
    equipmentOptionsLoadingMore,
    loadMoreEquipment,
    handleEquipmentSearchChange,
    technicianOptions,
    technicianOptionsLoading,
    technicianOptionsLoadingMore,
    loadMoreTechnicians,
    handleTechnicianSearchChange,
  } = useClientCertificateList();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);

  const exportFlow = useCertificateExport();
  const { isExporting, handleExport: runExport } = exportFlow;

  // Exports every row matching the committed search + filters — identical
  // params to the list fetch, minus pagination (export covers all rows).
  const handleExport = useCallback(async () => {
    await runExport(buildExportParams(appliedFilters), items.length);
  }, [appliedFilters, items.length, runExport]);

  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (accessDenied && onAccessDenied) {
      onAccessDenied();
    }
  }, [accessDenied, onAccessDenied]);

  const filterCount = useMemo(
    () => countAppliedFilters(appliedFilters),
    [appliedFilters]
  );

  const handleApplyAndClose = useCallback(() => {
    handleApplyFilters();
    setIsFilterOpen(false);
  }, [handleApplyFilters]);

  const handleClearAndClose = useCallback(() => {
    handleClearFilters();
    setIsFilterOpen(false);
  }, [handleClearFilters]);

  useEffect(() => {
    if (!isFilterOpen) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(e.target as Node) &&
        !(e.target as Element).closest?.('[data-date-picker-portal]')
      ) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isFilterOpen]);

  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [openMenuRowId]);

  const columns: Column<CertificateListItem>[] = useMemo(
    () =>
      buildCertificateColumns({
        canView: true,
        openMenuRowId,
        setOpenMenuRowId,
        menuOpensUpward,
        setMenuOpensUpward,
        menuAnchorRef,
        tableScrollContainerRef,
        navigate,
      }),
    [openMenuRowId, menuOpensUpward, navigate]
  );

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);
  const hasSearch = search.trim() !== '';
  const hasFilter = filterCount > 0;

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    items.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !hasSearch &&
    !hasFilter;

  const showNoResults =
    !loading &&
    !isSearchClearing &&
    !hasNoData &&
    items.length === 0 &&
    (hasSearch || hasFilter);

  const filterDropdowns = (
    <>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-[#181A1B] leading-5 tracking-[0.10px] wrap-break-word">
          {CERTIFICATE_LABELS.CLIENT_CERTS_FILTER_EQUIPMENT_LABEL}
        </p>
        <AsyncMultiSelectDropdown
          label={CERTIFICATE_LABELS.CLIENT_CERTS_FILTER_EQUIPMENT_PLACEHOLDER}
          items={equipmentOptions.map((opt) => ({
            id: opt.id,
            label: `${opt.name} [${opt.equipment_id}]`,
          }))}
          selectedIds={equipmentIds}
          loading={equipmentOptionsLoading}
          loadingMore={equipmentOptionsLoadingMore}
          onSelectionChange={setEquipmentIds}
          onSearchChange={handleEquipmentSearchChange}
          onScrollEnd={loadMoreEquipment}
          toolTip={false}
        />
      </div>

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium text-[#181A1B] leading-5 tracking-[0.10px] wrap-break-word">
          {CERTIFICATE_LABELS.CLIENT_CERTS_FILTER_TECHNICIAN_LABEL}
        </p>
        <AsyncMultiSelectDropdown
          label={CERTIFICATE_LABELS.CLIENT_CERTS_FILTER_TECHNICIAN_PLACEHOLDER}
          items={technicianOptions.map((t) => ({
            id: t.id,
            label: `${t.name} [${t.technician_id}]`,
          }))}
          selectedIds={technicianIds}
          loading={technicianOptionsLoading}
          loadingMore={technicianOptionsLoadingMore}
          onSelectionChange={setTechnicianIds}
          onSearchChange={handleTechnicianSearchChange}
          onScrollEnd={loadMoreTechnicians}
          toolTip={false}
        />
      </div>
    </>
  );

  if (fetchError && !loading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-3 py-16">
        <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
          {CERTIFICATE_LABELS.MY_CERTS_FETCH_ERROR}
        </p>
      </div>
    );
  }

  return (
    <>
      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={CERTIFICATE_LABELS.CERTS_TAB_SEARCH_PLACEHOLDER}
          />
          <div className="flex items-center gap-2 shrink-0">
            <div ref={filterContainerRef} className="relative">
              <FilterBar
                onClick={() => setIsFilterOpen((prev) => !prev)}
                isOpen={isFilterOpen}
                filterCount={filterCount}
              />
              <FilterPopup
                isOpen={isFilterOpen}
                onClose={() => {
                  setIsFilterOpen(false);
                  setEquipmentIds(appliedFilters.equipmentIds);
                  setTechnicianIds(appliedFilters.technicianIds);
                  setDateRange(appliedFilters.dateRange);
                  handleEquipmentSearchChange('');
                  handleTechnicianSearchChange('');
                }}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                dropdown={filterDropdowns}
                dropdownHasValue={
                  equipmentIds.length > 0 || technicianIds.length > 0
                }
                onApply={handleApplyAndClose}
                onClear={handleClearAndClose}
              />
            </div>
            <ExportButton
              onClick={() => void handleExport()}
              disabled={!canExport || isExporting || items.length === 0}
              {...(exportBlocked && {
                disabledTooltip:
                  CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
              })}
              colorClassName="bg-[#2A4455] text-white hover:bg-[#1f3340] hover:disabled:bg-[#2A4455]"
              iconColor="#FFFFFF"
            />
          </div>
        </div>
      )}

      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
      >
        {hasNoData ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-6 pb-16">
            <LottieAnimation
              animationData={myRequestsEmptyAnimation}
              loop
              width={300}
              aspectRatio="1 / 1"
            />
            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-base font-medium leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.CERTS_TAB_EMPTY_TITLE}
              </p>
              <p className="text-base font-normal leading-6 tracking-[0.5px] text-[#474A4C]">
                {CERTIFICATE_LABELS.CERTS_TAB_EMPTY_DESC}
              </p>
            </div>
          </div>
        ) : showNoResults ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-3">
            <LottieAnimation
              animationData={noResultsAnimation}
              width="clamp(160px, 18vw, 360px)"
              aspectRatio="432 / 350"
            />
            <div className="flex flex-col items-center gap-1">
              <p className="text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                {CERTIFICATE_LABELS.CERTS_TAB_NO_RESULTS_TITLE}
              </p>
              <p className="text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                {CERTIFICATE_LABELS.CERTS_TAB_NO_RESULTS_DESC}
              </p>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={items}
            loading={loading || isSearchClearing}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            onSort={handleSort}
            sortColumn={sortColumn}
            sortDirection={sortOrder}
            getStickyCellZIndex={(row, _idx, columnKey) =>
              columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
            }
            tableBodyStyle={{ gap: 4 }}
          />
        )}
      </div>

      {!hasNoData && totalCount > 10 && (
        <div className="shrink-0 px-4 -mx-4">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <p className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </p>
          )}
        </div>
      )}

      <CertificateExportSnackbars {...exportFlow} />
    </>
  );
}

interface CertificateListProps {
  clientMode?: boolean;
  onAccessDenied?: (() => void) | undefined;
}

export default function CertificateList({
  clientMode = false,
  onAccessDenied,
}: CertificateListProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();

  const canTemplateList = privileges.certificateTemplateList ?? false;
  const canTemplateCreate = privileges.certificateTemplateCreate ?? false;
  const canTemplateView = privileges.certificateTemplateView ?? false;
  const canTemplateEdit = privileges.certificateTemplateEdit ?? false;
  const canCertList = privileges.certificateList ?? false;
  const canCertView = privileges.certificateView ?? false;

  const tabParam = searchParams.get('tab');
  const defaultTab: ActiveTab =
    !canTemplateList && canCertList ? 'certyfikaty' : 'szablony';
  const activeTab: ActiveTab = (() => {
    if (tabParam === 'certyfikaty') {
      return canCertList ? 'certyfikaty' : defaultTab;
    }
    if (tabParam === 'szablony') {
      return canTemplateList ? 'szablony' : defaultTab;
    }
    return defaultTab;
  })();

  const handleTabChange = (tab: ActiveTab) => {
    const next = new URLSearchParams();
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const handleCreateClick = useCallback(() => {
    navigate('/Certyfikaty/Utwórz-szablon-certyfikatu');
  }, [navigate]);

  if (clientMode) {
    return (
      <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
        <div className="flex items-center justify-between shrink-0 py-2.5">
          <div className="text-[#181A1B] text-[22px] font-normal leading-7">
            {CERTIFICATE_LABELS.PAGE_TITLE}
          </div>
        </div>
        <ClientCertificatesTab onAccessDenied={onAccessDenied} />
      </div>
    );
  }

  // Return null (not a spinner) — privileges default all-false while loading, so
  // rendering early would flash a 403 page before the fetch completes.
  if (privilegesLoading) {
    return null;
  }

  if (!canTemplateList && !canCertList) {
    return <ErrorPage403 />;
  }

  const tabsWithPrivileges = TABS.map((tab) => {
    if (tab.id === 'szablony' && !canTemplateList) {
      return {
        ...tab,
        disabled: true,
        disabledTooltip: CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
      };
    }
    if (tab.id === 'certyfikaty' && !canCertList) {
      return {
        ...tab,
        disabled: true,
        disabledTooltip: CERTIFICATE_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP,
      };
    }
    return tab;
  });

  return (
    <div className="w-full h-full min-h-0 flex flex-col p-4 bg-white shadow-[0px_2px_4px_rgba(0,0,0,0.08)] rounded gap-4 overflow-hidden">
      <div className="flex items-center justify-between shrink-0 py-2.5">
        <div className="text-[#181A1B] text-[22px] font-normal leading-7">
          {CERTIFICATE_LABELS.PAGE_TITLE}
        </div>
      </div>

      <div className="shrink-0">
        <TabToggle
          tabs={tabsWithPrivileges}
          activeTab={activeTab}
          onChange={handleTabChange}
          ariaLabel={CERTIFICATE_LABELS.PAGE_TITLE}
        />
      </div>

      {/* No activeTab === 'certyfikaty' check needed — defaultTab guarantees we
          only land here when canCertList is true (or both tabs are locked out,
          in which case the 403 guard above already returned). */}
      {activeTab === 'szablony' && canTemplateList ? (
        <TemplatesTab
          onCreateClick={handleCreateClick}
          canCreate={canTemplateCreate}
          canView={canTemplateView}
          canEdit={canTemplateEdit}
        />
      ) : canCertList ? (
        // TODO: Once certificate management admin backend is implemented, manually test:
        // - ADMIN with certificateList=true can load certificates
        // - ADMIN with certificateList=false sees Certyfikaty tab disabled
        // - ADMIN with only certificateList=true (no certificateTemplateList) lands here by default
        // - SUPER_ADMIN sees both tabs enabled
        <CertificatesTab canView={canCertView} />
      ) : null}
    </div>
  );
}
